import { Inject, Injectable } from '@angular/core';
import { FormControlDefinition } from '../model/form-control-definition.model';
import { FormModel } from '../model/form.model';
import { CONSTANTS } from '../../config/constants.module';
import moment from 'moment';

/**
 * @copyright Schweizerische Bundesbahnen SBB
 * @class DetailViewFormBuilder
 * @version 0.0.1
 * @since Oktober 2017
 * @description The DetailViewBuilder provides JSON Object for usage in Detailview component.
 */

@Injectable({
  providedIn: 'root',
})
export class DetailViewFormBuilderService {
  constructor(@Inject(CONSTANTS) private _CONST: any) {}

  /**
   * @name buildFormJson
   * @description build json object  to create the form based on it
   * @param serviceFields
   * @param formFields
   */
  public buildFormJson(serviceFields, formFields): any {
    // initialize an empty json object
    const _form = this.createNewForm();
    // filter db/mapservice fields based on config form fields/inputfields (_filteredServiceFields: fields from service)
    const _filteredServiceFields = this.filterFormFields(serviceFields, formFields);
    if (!_filteredServiceFields) {
      return false;
    }
    try {
      for (let i = 0; i < _filteredServiceFields.length; i++) {
        if (
          _filteredServiceFields[i].component &&
          _filteredServiceFields[i].component === this._CONST.FIELD_TYPE.groupfields
        ) {
          const formCtrlDefGroupFields = this.getFormControlDefinitionForGroupFields(
            _filteredServiceFields[i],
            i
          );
          _form[0].form_fields.push(formCtrlDefGroupFields);
        } else {
          // get input field from configuration fields which corresponds to the service field
          const configField = formFields.filter(
            f => Object.keys(f).indexOf(_filteredServiceFields[i].name) !== -1
          )[0];
          const _formControlDef = this.getFromControlDefinition(
            configField,
            i,
            _filteredServiceFields[i]
          );
          _form[0].form_fields.push(_formControlDef);
        }
      }
      return _form[0];
    } catch (error) {
      throw Error('buildFormJson: ' + error);
    }
  }

  /**
   * @function createNewForm
   * @description create a new form json object
   */
  private createNewForm(): Array<any> {
    const _form = new Array<FormModel>();
    const _formModel = new FormModel();
    _formModel.form_id = '';
    _formModel.form_name = 'Attribute Editor';
    _formModel.form_fields = [];
    _form.push(_formModel);
    return _form;
  }

  /**
   * @function getFromControlDefinition
   * @description get options for selectbox from field domain (codedvalues)
   * @param configField
   * @param id
   * @param filteredServiceField
   */
  private getFromControlDefinition(configField: any, id: number, filteredServiceField): any {
    try {
      const _formControlDefinition = new FormControlDefinition();
      // get multi property form config if exist to create textarea
      const _multi: boolean = filteredServiceField.length > 255;

      _formControlDefinition.field_id = id;
      _formControlDefinition.field_label = configField[filteredServiceField.name];
      _formControlDefinition.field_name = filteredServiceField.name.toUpperCase();
      _formControlDefinition.field_component = this._CONST.FIELD_TYPE[configField.component];
      _formControlDefinition.field_required =
        configField.required != null ? configField.required : false;
      _formControlDefinition.field_editable =
        configField.editable != null ? configField.editable : true;
      _formControlDefinition.field_type = configField.type != null ? configField.type : null;
      _formControlDefinition.max_length =
        configField.max_length != null ? configField.max_length : null;
      _formControlDefinition.min_length =
        configField.min_length != null ? configField.min_length : null;
      _formControlDefinition.field_multi = _multi;
      _formControlDefinition.field_search =
        configField.searchField != null ? configField.searchField : null;
      _formControlDefinition.field_float = configField.float != null ? configField.float : null;

      // add options to the created field object
      if (
        _formControlDefinition.field_component === this._CONST.FIELD_TYPE.selectbox ||
        _formControlDefinition.field_component === this._CONST.FIELD_TYPE.radiobutton ||
        _formControlDefinition.field_component === this._CONST.FIELD_TYPE.textview
      ) {
        _formControlDefinition['opts'] = this.getSelectControlOptions(filteredServiceField);
        if (configField.onchange) {
          _formControlDefinition['field_onchange'] = configField.onchange;
        } else {
          _formControlDefinition['field_onchange'] = null;
        }
      }
      return _formControlDefinition;
    } catch (error) {
      throw Error('getFromControlDefinition: ' + error);
    }
  }

  /**
   * @function getSelectControlOptions
   * @description get options for selectbox from field domain (codedvalues)
   * @param filteredServiceField
   */
  private getSelectControlOptions(filteredServiceField): any {
    try {
      let _options = [];
      // if service field have domain than create options for select or radio box
      if (
        filteredServiceField.domain != null &&
        filteredServiceField.domain.codedValues.length > 0
      ) {
        _options = this.getFieldOptions(filteredServiceField.domain.codedValues);
      }
      return _options;
    } catch (error) {
      throw Error('getSelectControlOptions: ' + error);
    }
  }

  /**
   * @function mapAttributesInCorrectFieldType
   * @description  Iteriert über alle Properties des ausgewählten Features von der Esri-Service und mappt  die korrekte Datentypen
   * der Properties auf HTML Input Fields.
   * @param totalPropFromDB
   * @param inputFields
   */
  public mapAttributesInCorrectFieldType(totalPropFromDB, inputFields) {
    // iteriert über alle Properties des ausgewählten Feature von EsriService
    totalPropFromDB.forEach(currentProp => {
      if (!inputFields[currentProp.alias]) {
        return;
      }

      // parse attribute values from string to integer
      if (currentProp.type === 'esriFieldTypeOID' || currentProp.type === 'esriFieldTypeInteger') {
        inputFields[currentProp.alias] = parseInt(inputFields[currentProp.alias]);
      } else if (currentProp.type === 'esriFieldTypeSmallInteger') {
        // true oder false wenn es sich um ein checkbox handelt sonst ist ein radio und liefert den richtigen Wert
        if (inputFields[currentProp.alias] === true) {
          inputFields[currentProp.alias] = 1;
        } else if (inputFields[currentProp.alias] === false) {
          inputFields[currentProp.alias] = 0;
        }
      } else if (currentProp.type === 'esriFieldTypeDouble') {
        // Parse Input Field als float type
        inputFields[currentProp.alias] = parseFloat(inputFields[currentProp.alias]);
      } else if (currentProp.type === 'esriFieldTypeDate') {
        inputFields[currentProp.alias] = this.convertDateToEsriDateType(inputFields[currentProp.alias]);
      }
    });
  }

  /**
   * @return number esri date
   * @description convert date to esri date format
   * @param inputFieldValue
   */
  private convertDateToEsriDateType(inputFieldValue): number {
    const momentDate = moment(new Date(inputFieldValue));
    const utcDate = new Date(Date.UTC(momentDate.year(), momentDate.month(), momentDate.date()));
    return utcDate.valueOf();
  }

  /**
   * @function getFormControlDefinitionForGroupFields
   * @description get control fefinition for group fields
   * @param configField
   * @param id
   */
  private getFormControlDefinitionForGroupFields(configField: any, id: number): any {
    try {
      const formControlDefGroupFields = new FormControlDefinition();
      formControlDefGroupFields.field_id = id;
      formControlDefGroupFields.field_group_id = configField.groupId;
      formControlDefGroupFields.field_label = configField.name;
      formControlDefGroupFields.field_name = configField.name;
      formControlDefGroupFields.field_component = this._CONST.FIELD_TYPE[configField.component];
      return formControlDefGroupFields;
    } catch (error) {
      console.error('getFromControlDefinitionForGroupFields: ' + error);
    }
  }

  /**
   * @name filterFormFields
   * @return service form fields based on configuration input fields
   * @description filter map service fields based on configuration fields
   * @param serviceFields
   * @param formFields
   */
  private filterFormFields(serviceFields, formFields) {
    if (!serviceFields || !formFields) {
      return;
    }
    const _fields = [];
    formFields.forEach(field => {
      const _filterFormFields = serviceFields.filter(
        f => Object.keys(field).indexOf(f.name) !== -1
      );
      if (_filterFormFields.length > 0) {
        _fields.push(_filterFormFields[0]);
      } else {
        // If formField does not exist in serviceFields, only allow
        // predefined form formatting fields
        if (field.EMPTY_FIELD) {
          field.name = field.EMPTY_FIELD;
          _fields.push(field);
        } else if (field.FIELD_DIVIDER) {
          field.name = field.FIELD_DIVIDER;
          _fields.push(field);
        } else if (field.FULL_DIVIDER) {
          field.name = field.FULL_DIVIDER;
          _fields.push(field);
        } else if (field.GROUP_FIELD) {
          field.name = field.GROUP_FIELD;
          _fields.push(field);
        }
      }
    });
    return _fields;
  }

  /**
   * @name getFieldOptions
   * @return options list
   * @description create options list for selectbox or radiobutton
   * @param _codedValues
   */
  private getFieldOptions(_codedValues) {
    const _options = [];
    try {
      for (let i = 0; i < _codedValues.length; i++) {
        _options.push({
          label: _codedValues[i].name,
          value: _codedValues[i].code,
        });
      }
      return _options;
    } catch (error) {
      throw Error('getFieldOptions: ' + error);
    }
  }
}
